import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import './Signup.css';

const Signup = () => {
  const { register, loading, error, clearError } = useAuth();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear API error when user starts typing
    if (error) {
      clearError();
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = t('auth.signup.firstNameRequired');
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = t('auth.signup.lastNameRequired');
    }
    
    if (!formData.email) {
      newErrors.email = t('auth.signup.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('auth.signup.emailInvalid');
    }
    
    if (!formData.password) {
      newErrors.password = t('auth.signup.passwordRequired');
    } else if (formData.password.length < 6) {
      newErrors.password = t('auth.signup.passwordMin');
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.signup.confirmPasswordRequired');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.signup.passwordsNoMatch');
    }
    
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = t('auth.signup.agreeToTermsRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      // Call the backend API to register user
      const response = await register({
        fullName: formData.firstName + ' ' + formData.lastName,
        email: formData.email,
        password: formData.password,
        agreeToTerms: formData.agreeToTerms,
        role: 'user'
      });
      
      if (response.success) {
        // Registration successful - user is not automatically logged in
        console.log('Registration successful:', response);

        alert(t('auth.signup.accountCreatedAlert'));
        navigate('/');
      }
      
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({ general: error.message || t('auth.signup.registrationFailed') });
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h2>{t('auth.signup.joinAnandmaya')}</h2>
          <p>{t('auth.signup.subtitle')}</p>
        </div>
        
        <form onSubmit={handleSubmit} className="auth-form">
          {(errors.general || error) && (
            <div className="error-message general-error">
              {errors.general || error}
            </div>
          )}
          
          <div className="name-row">
            <div className="form-group">
              <label htmlFor="firstName">{t('auth.signup.firstName')}</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={errors.firstName ? 'error' : ''}
                placeholder={t('auth.signup.enterFirstName')}
              />
              {errors.firstName && <span className="error-text">{errors.firstName}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="lastName">{t('auth.signup.lastName')}</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={errors.lastName ? 'error' : ''}
                placeholder={t('auth.signup.enterLastName')}
              />
              {errors.lastName && <span className="error-text">{errors.lastName}</span>}
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="email">{t('auth.signup.emailAddress')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
              placeholder={t('auth.signup.enterEmail')}
            />
            {errors.email && <span className="error-text">{errors.email}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="password">{t('auth.signup.password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={errors.password ? 'error' : ''}
              placeholder={t('auth.signup.createPassword')}
            />
            {errors.password && <span className="error-text">{errors.password}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">{t('auth.signup.confirmPassword')}</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={errors.confirmPassword ? 'error' : ''}
              placeholder={t('auth.signup.confirmPasswordPlaceholder')}
            />
            {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
          </div>
          
          <div className="form-group">
            <label className="checkbox-container">
              <input 
                type="checkbox" 
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleChange}
              />
              <span className="checkmark"></span>
              {t('auth.signup.agreeToPrefix')} <Link to="/terms" className="terms-link">{t('auth.signup.termsAndConditions')}</Link> {t('auth.signup.and')} <Link to="/privacy" className="terms-link">{t('auth.signup.privacyPolicy')}</Link>
            </label>
            {errors.agreeToTerms && <span className="error-text">{errors.agreeToTerms}</span>}
          </div>
          
          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading ? t('auth.signup.creatingAccount') : t('auth.signup.createAccount')}
          </button>
        </form>
        
        <div className="auth-footer">
          <p>
            {t('auth.signup.alreadyHaveAccount')}
            <Link to="/login" className="auth-link">{t('auth.signup.signIn')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
